// @flow

import algoliasearch from 'algoliasearch';
import {sanitizePII} from 'docs/util';
import {
  searchResultsLoading,
  searchResultsLoaded,
} from 'docs/data/search/SearchReducer';
import type {Account, Dispatcher} from 'docs/data';
import type {SearchEvent} from 'docs/data/search/SearchReducer';
import {formatSearchResults} from '../formatSearchResults';
import {queryCopyableResults} from '../copyableResults';

// This code is used on the frontend to make a call to the Algolia API to fetch
// search results. The Algolia API does search on an Algolia Index
// which is created via the algolia crawler. See docs/scripts/algolia/crawler.js
// for the crawler code.

const ALGOLIA_APP_ID = 'Y4PFTTJ91H';
const ALGOLIA_API_KEY = '13cb82a358f97cd95f3321f14d6c3e44';
const ALGOLIA_INDEX = 'crawler_stripe-docs-v5';

const MAX_RESULTS = 7;

const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY);
const algolia = client.initIndex(ALGOLIA_INDEX);

async function searchAlgolia(query, options) {
  const sanitizedQuery = sanitizePII(query);
  try {
    return await algolia.search(sanitizedQuery, options);
  } catch (e) {
    // TODO(matv): add actual error handling
    return {hits: []};
  }
}

export const loadSearchResults = (
  dispatch: Dispatcher<SearchEvent>,
  account: Account,
) => async (query: string) => {
  dispatch(searchResultsLoading(query));

  // Setting optionalFilters to tags:docs promotes docs results
  // https://www.algolia.com/doc/guides/managing-results/rules/merchandising-and-promoting/how-to/how-to-promote-with-optional-filters/
  const optionalFilters = ['tags:docs'];
  const algoliaOptions: {[string]: any} = {
    optionalFilters,
    ruleContexts: 'docs',
    attributesToHighlight: 'hierarchy,content,section_title',
    highlightPreTag: '<strong>',
    highlightPostTag: '</strong>',
    hitsPerPage: MAX_RESULTS,
  };

  const algoliaResults = await searchAlgolia(query, algoliaOptions);

  const formattedAlgoliaResults = formatSearchResults(algoliaResults);
  const copyableResults = queryCopyableResults(query, account);

  const results = [
    ...copyableResults,
    ...formattedAlgoliaResults.slice(0, MAX_RESULTS - copyableResults.length),
  ];

  dispatch(searchResultsLoaded(results));

  return results;
};
